// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { ErrorCode } from '@errorHandler/constants';
import { Alert, Icon } from '@imprivata-cloud/design-system';
import { Input } from 'antd';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isObject } from '@/utils/utils';
import './OtpChallenge.less';

export const OTP_EXPIRY_SECONDS = 300;
const OTP_LENGTH = 6;
const BANNER_AUTO_CLEAR_MS = 15000;

type OtpChallengeSeverity = 'success' | 'error' | 'info';

export const OTP_CHALLENGE_STATUS = {
  SENT: 'sent',
  INCORRECT: 'incorrect',
  INVALIDATED: 'invalidated',
  EXPIRED: 'expired',
} as const;
export type OtpChallengeStatus = (typeof OTP_CHALLENGE_STATUS)[keyof typeof OTP_CHALLENGE_STATUS];

export const OTP_CHALLENGE_SEVERITY = {
  [OTP_CHALLENGE_STATUS.SENT]: 'info',
  [OTP_CHALLENGE_STATUS.INCORRECT]: 'error',
  [OTP_CHALLENGE_STATUS.INVALIDATED]: 'error',
  [OTP_CHALLENGE_STATUS.EXPIRED]: 'error',
} satisfies Record<OtpChallengeStatus, OtpChallengeSeverity>;

export const OTP_OUTCOME_CODE = {
  [ErrorCode.INVALID_OTP]: OTP_CHALLENGE_STATUS.INCORRECT,
  [ErrorCode.OTP_INVALID]: OTP_CHALLENGE_STATUS.INCORRECT,
  [ErrorCode.MAX_ATTEMPTS_EXCEEDED]: OTP_CHALLENGE_STATUS.INVALIDATED,
  [ErrorCode.OTP_EXPIRED]: OTP_CHALLENGE_STATUS.EXPIRED,
} satisfies Record<string, OtpChallengeStatus>;

const getErrorCode = (error: unknown): string | null =>
  isObject(error) && typeof error.code === 'string' ? error.code : null;

export const mapErrorCodeToStatus = (error: unknown): OtpChallengeStatus | null => {
  const code = getErrorCode(error);
  return code !== null && Object.hasOwn(OTP_OUTCOME_CODE, code)
    ? OTP_OUTCOME_CODE[code as keyof typeof OTP_OUTCOME_CODE]
    : null;
};

const formatCountdown = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export type OtpChallengeCopy = Record<OtpChallengeStatus, string> & {
  resendLink: string;
  resendPrompt?: string;
  countdown?: string;
  attemptsRemaining?: string;
};

type OtpChallengeActionsContext = { canSubmit: boolean; submit: () => void };

type OtpChallengeProps = {
  heading: ReactNode;
  groupLabel: string;
  copy: OtpChallengeCopy;
  statusVariant: 'inline' | 'banner';
  codeIssuedAtMs: number | null;
  status: OtpChallengeStatus | null;
  attemptsRemaining?: number;
  onStatusClear: () => void;
  onSubmit: (code: string) => void | Promise<void>;
  onResend: () => void | Promise<void>;
  isSubmitting?: boolean;
  actions: (context: OtpChallengeActionsContext) => ReactNode;
};

export const OtpChallenge = ({
  heading,
  groupLabel,
  copy,
  statusVariant,
  codeIssuedAtMs,
  status,
  attemptsRemaining,
  onStatusClear,
  onSubmit,
  onResend,
  isSubmitting,
  actions,
}: OtpChallengeProps) => {
  const { t } = useTranslation();
  const [cells, setCells] = useState<string[]>([]);
  const [resendCount, setResendCount] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [isResending, setIsResending] = useState(false);

  const code = cells.join('');
  const isCodeComplete = cells.length === OTP_LENGTH && cells.every(cell => /^\d$/.test(cell));
  const isErrorStatus = status !== null && OTP_CHALLENGE_SEVERITY[status] === 'error';

  const remainingSeconds =
    codeIssuedAtMs === null
      ? null
      : Math.max(0, OTP_EXPIRY_SECONDS - Math.floor((nowMs - codeIssuedAtMs) / 1000));

  const isCodeExpired = remainingSeconds === 0;
  const isDerivedExpired = remainingSeconds === 0 && status !== OTP_CHALLENGE_STATUS.EXPIRED;

  const canSubmit = isCodeComplete && !isSubmitting && !isCodeExpired;

  const submit = useCallback(() => {
    if (canSubmit) {
      onSubmit(code);
    }
  }, [canSubmit, code, onSubmit]);

  const lastAutoSubmittedCodeRef = useRef<string | null>(null);

  const handleInput = useCallback(
    (value: string[]) => {
      setCells(value);
      const complete = value.length === OTP_LENGTH && value.every(cell => /^\d$/.test(cell));
      const joined = value.join('');
      if (
        statusVariant === 'inline' &&
        complete &&
        !isSubmitting &&
        !isCodeExpired &&
        lastAutoSubmittedCodeRef.current !== joined
      ) {
        lastAutoSubmittedCodeRef.current = joined;
        onSubmit(joined);
      }
    },
    [statusVariant, isSubmitting, isCodeExpired, onSubmit],
  );

  const handleResend = useCallback(async () => {
    if (isResending) {
      return;
    }
    setIsResending(true);
    setCells([]);
    lastAutoSubmittedCodeRef.current = null;
    setResendCount(count => count + 1);
    try {
      await onResend();
    } finally {
      setIsResending(false);
    }
  }, [isResending, onResend]);

  useEffect(() => {
    if (codeIssuedAtMs === null) {
      return undefined;
    }

    const remainderAt = (atMs: number) =>
      Math.max(0, OTP_EXPIRY_SECONDS - Math.floor((atMs - codeIssuedAtMs) / 1000));

    setNowMs(Date.now());
    if (remainderAt(Date.now()) === 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const tickMs = Date.now();
      setNowMs(tickMs);
      if (remainderAt(tickMs) === 0) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [codeIssuedAtMs]);

  useEffect(() => {
    if (status === null) {
      return undefined;
    }
    if (statusVariant !== 'banner' && status !== OTP_CHALLENGE_STATUS.SENT) {
      return undefined;
    }

    const timeoutId = window.setTimeout(onStatusClear, BANNER_AUTO_CLEAR_MS);

    return () => window.clearTimeout(timeoutId);
  }, [statusVariant, status, onStatusClear]);

  const showErrorInSlotA = statusVariant === 'inline' && isErrorStatus && !isDerivedExpired;
  const showCountdownInSlotA = !isErrorStatus && !isDerivedExpired && remainingSeconds !== null;

  return (
    <div className={`otp-challenge otp-challenge--${statusVariant}`}>
      {statusVariant === 'banner' && status !== null && (
        <Alert
          status={OTP_CHALLENGE_SEVERITY[status]}
          title={t(copy[status])}
          onClose={onStatusClear}
          className="otp-challenge__banner"
        />
      )}

      <div className="otp-challenge__heading">{heading}</div>

      <Input.OTP
        key={resendCount}
        length={OTP_LENGTH}
        status={showErrorInSlotA ? 'error' : undefined}
        onInput={handleInput}
        aria-label={groupLabel}
        inputMode="numeric"
        formatter={value => value.replace(/[^0-9 ]/g, '')}
        autoFocus
      />

      <div className="otp-challenge__slot-a">
        {showErrorInSlotA && status !== null && (
          <div className="otp-challenge__status-group">
            <span className="otp-challenge__status" role="alert">
              <Icon type={OTP_CHALLENGE_SEVERITY[status]} />
              {t(copy[status])}
            </span>
            {status === OTP_CHALLENGE_STATUS.INCORRECT &&
              copy.attemptsRemaining &&
              attemptsRemaining !== undefined && (
                <span className="otp-challenge__attempts-remaining">
                  {t(copy.attemptsRemaining, { count: attemptsRemaining })}
                </span>
              )}
          </div>
        )}
        {isDerivedExpired && (
          <span className="otp-challenge__status" role="alert">
            <Icon type={OTP_CHALLENGE_SEVERITY[OTP_CHALLENGE_STATUS.EXPIRED]} />
            {t(copy[OTP_CHALLENGE_STATUS.EXPIRED])}
          </span>
        )}
        {showCountdownInSlotA && (
          <span className="otp-challenge__countdown" aria-live="polite">
            {copy.countdown
              ? t(copy.countdown, { time: formatCountdown(remainingSeconds) })
              : formatCountdown(remainingSeconds)}
          </span>
        )}
      </div>

      <div className="otp-challenge__slot-b">
        {copy.resendPrompt && <span>{t(copy.resendPrompt)}</span>}
        {statusVariant === 'inline' && status === OTP_CHALLENGE_STATUS.SENT ? (
          <span className="otp-challenge__resend-sent">
            <Icon type="success" />
            {t(copy[OTP_CHALLENGE_STATUS.SENT])}
          </span>
        ) : (
          <button type="button" className="otp-challenge__resend-link" onClick={handleResend} disabled={isResending}>
            {t(copy.resendLink)}
          </button>
        )}
      </div>

      <div className="otp-challenge__actions">{actions({ canSubmit, submit })}</div>
    </div>
  );
};
